import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useLivePoll } from "../lib/useLivePoll";
import { Lock } from "lucide-react";
import { SILK_COLOURS } from "../lib/constants";
import Skeleton from "../components/Skeleton";

export default function AllPicksTab({ group }) {
  const [dates, setDates] = useState([]);
  const [activeDate, setActiveDate] = useState(null);
  const [races, setRaces] = useState([]);
  const [members, setMembers] = useState([]);
  const [picks, setPicks] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadDates = useCallback(async () => {
    if (!group) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/meeting-dates`);
      setDates(data.meeting_dates);
      setActiveDate((cur) => {
        if (cur && data.meeting_dates.find((d) => d.meeting_date === cur)) return cur;
        const upcoming = data.meeting_dates.find((d) => !d.cutoff_passed);
        return (upcoming || data.meeting_dates[data.meeting_dates.length - 1])?.meeting_date || null;
      });
    } catch (_) {}
  }, [group]);

  const loadAll = useCallback(async () => {
    if (!group || !activeDate) { setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: rd }, { data: md }, { data: pd }] = await Promise.all([
        api.get(`/groups/${group.id}/races`, { params: { date: activeDate } }),
        api.get(`/groups/${group.id}/members`),
        api.get(`/groups/${group.id}/picks/all`, { params: { date: activeDate } }),
      ]);
      setRaces(rd.races);
      setMembers(md.members.filter((m) => m.approved && !m.is_owner || m.approved));
      setPicks(pd.picks);
      setRevealed(pd.revealed);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load"));
    } finally { setLoading(false); }
  }, [group, activeDate]);

  useEffect(() => { loadDates(); }, [loadDates]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // pick lookup: race_id -> user_id -> pick
  const pickMatrix = useMemo(() => {
    const m = new Map();
    picks.forEach((p) => {
      if (!m.has(p.race_id)) m.set(p.race_id, new Map());
      m.get(p.race_id).set(p.user_id, p);
    });
    return m;
  }, [picks]);

  if (!group) return null;

  return (
    <div className="px-4 pt-4 space-y-4 pb-24" data-testid="all-picks-tab-content">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-extrabold text-lg">All Picks</h3>
        <div className="flex items-center gap-1.5" data-testid="reveal-state">
          {revealed ? (
            <span className="text-[9px] font-black uppercase tracking-wider bg-[#2f9e44]/10 text-[#2f9e44] border border-[#2f9e44]/25 rounded-full px-2 py-0.5">
              Revealed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-[#f8f9fa] text-[#6c757d] border border-[#e9ecef] rounded-full px-2 py-0.5">
              <Lock className="w-3 h-3" strokeWidth={2.5} /> Hidden until cutoff
            </span>
          )}
        </div>
      </div>

      {dates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4">
          {dates.map((d) => (
            <button
              key={d.meeting_date}
              onClick={() => setActiveDate(d.meeting_date)}
              data-testid={`allpicks-date-${d.meeting_date}`}
              className={`shrink-0 px-3 py-1.5 rounded-full font-bold text-xs border transition-transform active:scale-95 ${
                d.meeting_date === activeDate
                  ? "bg-[#1971c2] text-white border-[#1971c2]"
                  : "bg-white text-[#212529] border-[#e9ecef]"
              }`}
            >
              {d.meeting_date} · {d.race_count}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <Skeleton rows={4} height={60} testId="allpicks-skeleton" />
      ) : races.length === 0 ? (
        <div className="bg-white border border-[#e9ecef] rounded-2xl p-8 text-center" data-testid="allpicks-empty">
          <div className="text-5xl mb-3">👀</div>
          <div className="font-display font-extrabold text-xl">Nothing to peek at yet</div>
          <p className="text-sm text-[#6c757d] mt-2">Load a race card first.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e9ecef] rounded-2xl overflow-hidden">
          {/* Result headers list */}
          {races.some((r) => r.result) && (
            <div className="p-3 border-b border-[#e9ecef] space-y-1.5 bg-[#f8f9fa]" data-testid="all-picks-results-header">
              {races.filter((r) => r.result).map((r) => {
                const rs = r.result;
                const nameOf = (n) => r.horses.find((h) => h.horse_number === n)?.name || `#${n}`;
                const winPts = (rs.win_dividend + rs.place_dividend).toFixed(2);
                return (
                  <div key={r.id} className="text-[11px] leading-snug">
                    <span className="font-black">{r.track}·R{r.race_number}</span>
                    <span className="text-[#6c757d]"> · </span>
                    <span>🥇 {nameOf(rs.first_horse_number)} Win ${rs.win_dividend.toFixed(2)} + Place ${rs.place_dividend.toFixed(2)} = ${winPts} pts</span>
                    <span className="text-[#6c757d]"> · </span>
                    <span>🥈 {nameOf(rs.second_horse_number)} ${rs.second_dividend.toFixed(2)}</span>
                    <span className="text-[#6c757d]"> · </span>
                    <span>🥉 {nameOf(rs.third_horse_number)} {rs.third_dividend_paid ? `$${(rs.third_dividend || 0).toFixed(2)}` : <span className="text-[#c92a2a] font-black">No 3rd Dividend</span>}</span>
                    <div className="text-[9px] text-[#6c757d] font-semibold mt-0.5">
                      Official source: Racing Australia · Dividends from {rs.source}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="overflow-x-auto" data-testid="all-picks-table">
            <table className="min-w-full text-sm">
              <thead className="bg-[#f8f9fa] text-[10px] uppercase tracking-wider text-[#6c757d]">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-[#f8f9fa] z-10">Member</th>
                  {races.map((r) => (
                    <th key={r.id} className="text-left px-3 py-2 whitespace-nowrap">
                      {r.track.split(" ")[0]}·R{r.race_number}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((mem) => (
                  <tr key={mem.user_id} className="border-t border-[#e9ecef]" data-testid={`allpicks-row-${mem.user_id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[#f8f9fa] border border-[#e9ecef] flex items-center justify-center text-lg shrink-0">
                          {mem.avatar_emoji}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold truncate max-w-[120px]">{mem.display_name}</div>
                        </div>
                      </div>
                    </td>
                    {races.map((r) => {
                      const p = pickMatrix.get(r.id)?.get(mem.user_id);
                      if (!p) {
                        return (
                          <td key={r.id} className="px-3 py-2 text-[#adb5bd] text-xs">—</td>
                        );
                      }
                      const horse = r.horses.find((h) => h.horse_number === p.horse_number);
                      const silk = SILK_COLOURS[((horse?.horse_number || 1) - 1) % 12];
                      const finished = r.status === "finished" && r.result;
                      let emoji = null;
                      if (finished) {
                        if (p.is_win) emoji = "🥇";
                        else if (p.horse_number === r.result.second_horse_number) emoji = "🥈";
                        else if (p.horse_number === r.result.third_horse_number) emoji = "🥉";
                        else emoji = "❌";
                      }
                      return (
                        <td key={r.id} className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="w-6 h-6 rounded flex items-center justify-center text-white font-black text-[10px] shrink-0"
                              style={{ backgroundColor: silk }}
                            >
                              {horse?.horse_number}
                            </div>
                            <div className="min-w-0">
                              <div className="text-xs font-bold truncate max-w-[100px]">{horse?.name || "—"}</div>
                              <div className="text-[9px] flex items-center gap-1">
                                {finished && (
                                  <>
                                    <span>{emoji}</span>
                                    <span className={`font-black tabular-nums ${p.no_third_dividend ? "text-[#c92a2a]" : ""}`}>
                                      {(p.points_earned || 0).toFixed(2)}
                                    </span>
                                  </>
                                )}
                                {p.wildcard_applied && (
                                  <span className="font-black uppercase tracking-wider text-[#6741d9]">⚡</span>
                                )}
                                {p.scratching_replacement && !finished && (
                                  <span className="font-black uppercase tracking-wider text-[#c07800]">Swap</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!revealed && (
                  <tr>
                    <td colSpan={races.length + 1} className="px-3 py-4 text-center text-[#6c757d] text-xs font-semibold bg-[#f8f9fa]" data-testid="allpicks-hidden-note">
                      🔒 Others' picks hidden until picks close
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
